
import {
  Address,
  Location,
  Trip,
  TripInfoMutation,
  TripLog,
  TripMutation,
  TripSource
} from '@clients/myTrips';
import TripDetail from '@components/trip-detail/trip-detail/TripDetail.vue';
import { AddType } from '@components/trip-detail/trip-detail/TripDetailTabs.vue';
import { MIN_ODOMETER_GAP } from '@util/odometerService';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { scrollIntoPage } from '@util/scrollUtils';

const TriplogModule = namespace('Triplog');

@Component({
  name: "TripAdd",
  components: {
    TripDetail
  }
})
export default class TripAdd extends Vue {

  private prevTrip: Trip = null;

  private nextTrip: Trip = null;

  private firstTrip: Trip = null;

  private lastTrip: Trip = null;

  private tripMutation: TripMutation = null;

  @TriplogModule.Action('fetchSingleTrip') fetchSingleTrip;

  @TriplogModule.Action('fetchLatestTrip') fetchLatestTrip;

  @TriplogModule.Action('fetchOldestTrip') fetchOldestTrip;

  @TriplogModule.Action('fetchVehicles') fetchTripLogs;

  @TriplogModule.Getter('getVehicles')
  private triplogs: TripLog[];

  async created() {
    await this.fetchTripLogs();

    const generalTripInfo = {
      vehicleId: parseInt(this.$route.query.vehicle as string),
      stops: [],
      source: TripSource.MOE,
      work: false,
      note: ''
    }

    const [prevTrip, nextTrip, firstTrip, lastTrip] = await Promise.all([
      this.$route.query.prevTripId ? this.fetchSingleTrip({ tripId: parseInt(this.$route.query.prevTripId as string) }) : Promise.resolve(null),
      this.$route.query.nextTripId ? this.fetchSingleTrip({ tripId: parseInt(this.$route.query.nextTripId as string) }) : Promise.resolve(null),
      !this.isGapFill ? this.fetchOldestTrip({ vehicleId: parseInt(this.$route.query.vehicle as string) }) : Promise.resolve(null),
      !this.isGapFill ? this.fetchLatestTrip({ vehicleId: parseInt(this.$route.query.vehicle as string) }) : Promise.resolve(null)
    ]);

    this.prevTrip = prevTrip;
    this.nextTrip = nextTrip;
    this.firstTrip = firstTrip;
    this.lastTrip = lastTrip;

    const startInfoAll = this.isGapFill ? (this.prevTrip || {}).endInfo : (this.lastTrip || {}).endInfo;
    const endInfoGap = this.isGapFill ? (this.nextTrip || {}).startInfo : null;
    const endInfoNoGap = !this.isGapFill ? (this.firstTrip || {}).startInfo : null;

    const startOdo = (startInfoAll || {}).odometer;
    const endOdo = (endInfoGap || {}).odometer;
    const endOdoNoGap = (endInfoNoGap || {}).odometer;

    if (!this.isGapFill && this.newestTripDisabled && this.firstTrip) {
      this.tripMutation = {
        ...generalTripInfo,
        startInfo: {
          odometer: null,
          timestamp: null,
          address: null,
          location: null
        } as unknown as TripInfoMutation,
        endInfo: {
          odometer: (endOdoNoGap || endOdoNoGap === 0) ? endOdoNoGap : null,
          timestamp: (endInfoNoGap || {}).timestamp ? endInfoNoGap.timestamp : null,
          address: (endInfoNoGap || {}).address as Address || null,
          location: (endInfoNoGap || {}).location as Location || null
        } as unknown as TripInfoMutation
      }
    } else if (this.firstTrip || this.lastTrip || this.isGapFill) {
      this.tripMutation = {
        ...generalTripInfo,
        startInfo: {
          odometer: (startOdo || startOdo === 0) ? startOdo : null,
          timestamp: (startInfoAll || {}).timestamp ? startInfoAll.timestamp : null,
          address: (startInfoAll || {}).address as Address || null,
          location: (startInfoAll || {}).location as Location || null
        } as unknown as TripInfoMutation,
        endInfo: {
          odometer: (endOdo || endOdo === 0) ? endOdo : null,
          timestamp: (endInfoGap || {}).timestamp ? endInfoGap.timestamp : new Date().toISOString(),
          address: (endInfoGap || {}).address as Address || null,
          location: (endInfoGap || {}).location as Location || null
        } as unknown as TripInfoMutation,
      } as TripMutation;
    } else {
      const timeStart = new Date();
      timeStart.setHours(timeStart.getHours() - 1);

      this.tripMutation = {
        ...generalTripInfo,
        startInfo: {
          odometer: 0,
          timestamp: timeStart.toISOString(),
          address: null,
          location: null
        } as unknown as TripInfoMutation,
        endInfo: {
          odometer: null,
          timestamp: new Date().toISOString(),
          address: null,
          location: null
        } as unknown as TripInfoMutation,
      } as TripMutation;
    }
  }

  get oldestTripDisabled() {
    return !this.isGapFill && this.firstTrip && this.firstTrip.startInfo.odometer <= 100;
  }

  get isGapFill() {
    return !!this.$route.query.prevTripId && !!this.$route.query.nextTripId;
  }

  get tripDefaults(): TripMutation {
    return this.tripMutation;
  }

  get newestTripDisabled() {
    return this.triplogs.some(t => t.vehicle.id === +this.$route.query.vehicle && t.vehicle.setupDone === true && t.smartConnected);
  }

  // Latest/Oldest Trip Tabs, only for no gap fill
  private resetMutation(pos: AddType, trip: TripMutation) {
    const startForMissing = pos === 'gap' ? (this.prevTrip || {}).endInfo : (this.lastTrip || {}).endInfo;
    const endForMissing = pos === 'gap' ? (this.nextTrip || {}).startInfo : (this.firstTrip || {}).startInfo;

    const startOdo = (startForMissing || {}).odometer;
    const endOdo = (endForMissing || {}).odometer;

    var tripMutationStartInfo = {
      odometer: null,
      timestamp: null,
      address: null,
      location: null
    } as unknown as TripInfoMutation

    var tripMutationEndInfo = {
      odometer: null,
      timestamp: null,
      address: null,
      location: null
    } as unknown as TripInfoMutation

    if (pos === 'last' || pos === 'gap') {
      tripMutationStartInfo = {
        odometer: (startOdo || startOdo === 0) ? startOdo : null,
        timestamp: (startForMissing || {}).timestamp ? startForMissing.timestamp : null,
        address: (startForMissing || {}).address as Address || null,
        location: (startForMissing || {}).location as Location || null
      } as unknown as TripInfoMutation
    }

    if (pos === 'first' || pos === 'gap') {
      tripMutationEndInfo = {
        odometer: (endOdo || endOdo === 0) ? endOdo : null,
        timestamp: (endForMissing || {}).timestamp ? endForMissing.timestamp : null,
        address: (endForMissing || {}).address as Address || null,
        location: (endForMissing || {}).location as Location || null
      } as unknown as TripInfoMutation
    }

    this.tripMutation = {
      ...trip,
      startInfo: tripMutationStartInfo,
      endInfo: tripMutationEndInfo,
      routeGeometry: undefined
    }

    if (pos === 'last') {
      this.tripMutation.endInfo.timestamp = new Date().toISOString()
    }
  }

  private addAnotherTrip(currentPos: AddType, createdTrip: Trip) {
    if (currentPos === 'gap') {
      if (this.nextTrip.startInfo.odometer - createdTrip.endInfo.odometer >= MIN_ODOMETER_GAP) {
        this.prevTrip = createdTrip;
      } else if (createdTrip.startInfo.odometer - this.prevTrip.endInfo.odometer >= MIN_ODOMETER_GAP) {
        this.nextTrip = createdTrip;
      }
    } else if (!this.lastTrip && !this.firstTrip) {
      this.lastTrip = createdTrip;
      this.firstTrip = createdTrip;
    } else if (currentPos === 'last') {
      this.lastTrip = createdTrip;
    } else if (currentPos === 'first') {
      this.firstTrip = createdTrip;
    }

    scrollIntoPage({ behavior: 'smooth' })
    this.resetMutation(currentPos, createdTrip);
  }
}
